import { useRef, useState, FC, ReactNode, RefObject } from 'react';
import { graphql, useFragment } from 'react-relay';

import { FavoritesItemProvider_item$key } from './__generated__/FavoritesItemProvider_item.graphql';

type Props = {
    item: FavoritesItemProvider_item$key;
    applyMouseEvents: boolean;
    className?: string;
    children: (args: {
        isFolderVisible?: boolean;
        showFolderPlusIcon?: boolean;
        onFavorited?: () => void;
        onUnFavorited?: () => void;
        onFolderActionStart?: () => void;
        onFolderActionEnd?: () => void;
    }) => ReactNode;
};

const FavoritesItemProvider: FC<Props> = ({
    item: itemRef,
    applyMouseEvents,
    className,
    children,
}) => {
    const item = useFragment(
        graphql`
            fragment FavoritesItemProvider_item on Item {
                serviceId
            }
        `,
        itemRef
    );

    const tileElementRef: RefObject<HTMLDivElement> = useRef(null);
    const [isMouseOver, setIsMouseOver] = useState(false);
    const [isHeartFilled, setIsHeartFilled] = useState(false);
    const [isFolderAction, setIsFolderAction] = useState(false);

    // We check if heart icon is filled or not by data-tn attribute
    // instead of refetching on mouse over to check if item is favorited.
    const isFavoritesHeartFilled = (): boolean =>
        tileElementRef?.current
            ?.querySelector(`[id=${item.serviceId || ''}-heart]`)
            ?.getAttribute('data-tn')
            ?.includes('saved') || false;

    const mouseEventProps = applyMouseEvents
        ? {
              onMouseEnter: () => {
                  setIsMouseOver(true);
                  setIsHeartFilled(isFavoritesHeartFilled());
              },
              onMouseLeave: () => setIsMouseOver(false),
          }
        : {};

    const childrenProps = applyMouseEvents
        ? {
              isFolderVisible: isHeartFilled && (isMouseOver || isFolderAction),
              showFolderPlusIcon: true,
              onFavorited: () => setIsHeartFilled(true),
              onUnFavorited: () => setIsHeartFilled(false),
              onFolderActionStart: () => setIsFolderAction(true),
              onFolderActionEnd: () => setIsFolderAction(false),
          }
        : {};

    return (
        <div className={className} ref={tileElementRef} {...mouseEventProps}>
            {children(childrenProps)}
        </div>
    );
};

export default FavoritesItemProvider;
